import { defineStore } from "pinia";
import type { Repas } from "~/models/repas";
import type { Menu } from "~/models/menu";
import type { Sauce } from "~/models/sauce";
import type { Dessert } from "~/models/dessert";

export interface CartItem {
  id: string;
  type: "regular" | "customized" | "menu";
  meal?: Repas; // Made optional as it's not present for menus
  menu?: Menu; // For menu items
  quantity: number;
  extras?: Array<{
    ingredient: {
      id: number;
      name: string;
      price: number;
      is_supplement: boolean;
      is_halal: boolean;
      is_vegan: boolean;
      image: string;
    };
    quantity: number;
  }>;
  // For menu items, store customizations
  menuCustomizations?: Array<{
    menuContentId: number;
    mealName?: string; // Added to store meal name for display
    extras: Array<{
      ingredient: {
        id: number;
        name: string;
        price: number;
        is_supplement: boolean;
        is_halal: boolean;
        is_vegan: boolean;
        image: string;
      };
      quantity: number;
    }>;
  }>;
  selectedSauces?: Array<{ sauce: Sauce; quantity: number }>;
  selectedDesserts?: Array<{ dessert: Dessert; quantity: number }>;
  totalPrice: number;
  addedAt: number;
  note?: string;
}

export const useCartStore = defineStore("cart", () => {
  const items = ref<CartItem[]>([]);
  const isOpen = ref(false);
  const deliveryInfo = ref<{
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: {
      street: string;
      postalCode: string;
      city: string;
      instructions?: string;
    };
  } | null>(null);

  // Load cart from localStorage on initialization
  const loadCart = () => {
    if (import.meta.client) {
      try {
        const stored = localStorage.getItem("resto-cart");
        if (stored) {
          if (stored) {
            const parsed = JSON.parse(stored);
            items.value = parsed.items || parsed; // Handle old format (array) vs new format (object)
            if (parsed.deliveryInfo) {
              deliveryInfo.value = parsed.deliveryInfo;
            }
          }
        }
      } catch (error) {
        console.error("Error loading cart from localStorage:", error);
        items.value = [];
      }
    }
  };

  // Save cart to localStorage
  const saveCart = () => {
    if (import.meta.client) {
      try {
        localStorage.setItem(
          "resto-cart",
          JSON.stringify({
            items: items.value,
            deliveryInfo: deliveryInfo.value,
          }),
        );
      } catch (error) {
        console.error("Error saving cart to localStorage:", error);
      }
    }
  };

  // Initialize cart on store creation
  loadCart();

  // Watch for changes and save to localStorage
  watch(items, saveCart, { deep: true });

  // Helper to calculate cost of extras with "First N free" logic
  const calculateLimitedExtrasCost = (
    items: Array<{ price: number; quantity: number }>,
    limit: number,
  ) => {
    const prices: number[] = [];
    items.forEach((item) => {
      for (let i = 0; i < item.quantity; i++) prices.push(item.price);
    });
    // Sort Descending (Expensive first are free)
    prices.sort((a, b) => b - a);
    // Skip first 'limit' items (Included)
    const toPay = prices.slice(limit);
    // Sum
    return toPay.reduce((a, b) => a + b, 0);
  };

  // Add regular meal to cart
  const addRegularMeal = (meal: Repas, quantity: number = 1) => {
    const existingItem = items.value.find(
      (item) =>
        item.type === "regular" &&
        item.meal?.id === meal.id &&
        item.meal?.name === meal.name,
    );

    if (existingItem && existingItem.meal) {
      existingItem.quantity += quantity;
      existingItem.totalPrice = existingItem.meal.price * existingItem.quantity;
    } else {
      const newItem: CartItem = {
        id: `regular-${meal.id}-${Date.now()}`,
        type: "regular",
        meal,
        quantity,
        totalPrice: meal.price * quantity,
        addedAt: Date.now(),
      };
      items.value.push(newItem);
    }
  };

  // Helper to parse original ingredients from meal (handles structured content and string)
  const parseOriginalIngredients = (meal: Repas): Record<string, number> => {
    const originalIngredients: Record<string, number> = {};

    // Method 1: Use structured content (Preferred)
    if (
      meal.content &&
      Array.isArray(meal.content) &&
      meal.content.length > 0
    ) {
      meal.content.forEach(
        (item: { ingredient?: { name: string }; quantity: number }) => {
          if (item.ingredient && item.ingredient.name) {
            originalIngredients[item.ingredient.name.toLowerCase()] =
              item.quantity;
          }
        },
      );
      return originalIngredients;
    }

    // Method 2: Fallback to string parsing
    if (meal.ingredients) {
      const ingredientStrings = meal.ingredients
        .split(",")
        .map((s: string) => s.trim());

      ingredientStrings.forEach((ingredientString: string) => {
        const match = ingredientString.match(/^(\d+)\s+(.+)$/);
        if (match) {
          const quantity = parseInt(match[1]);
          const ingredientName = match[2].trim();
          originalIngredients[ingredientName.toLowerCase()] = quantity;
        } else if (ingredientString.length > 0) {
          // Implicit quantity 1
          const ingredientName = ingredientString.trim();
          originalIngredients[ingredientName.toLowerCase()] = 1;
        }
      });
    }
    return originalIngredients;
  };

  // Add customized meal to cart
  const addCustomizedMeal = (
    meal: Repas,
    extras: Array<{
      ingredient: {
        id: number;
        name: string;
        price: number;
        is_supplement: boolean;
        is_halal: boolean;
        is_vegan: boolean;
        image: string;
      };
      quantity: number;
    }>,
    quantity: number = 1,
  ) => {
    const originalIngredients = parseOriginalIngredients(meal);

    // Calculate cost only for additional ingredients (beyond original)
    const additionalCost = extras.reduce((total, extra) => {
      const originalQuantity =
        originalIngredients[extra.ingredient.name.toLowerCase()] || 0;
      const additionalQuantity = Math.max(0, extra.quantity - originalQuantity);
      return total + extra.ingredient.price * additionalQuantity;
    }, 0);

    const totalPrice = (meal.price + additionalCost) * quantity;

    // For customized meals, we always create a new item since customizations are unique
    const newItem: CartItem = {
      id: `customized-${meal.id}-${Date.now()}`,
      type: "customized",
      meal,
      quantity,
      extras,
      totalPrice,
      addedAt: Date.now(),
    };
    items.value.push(newItem);
  };

  // Add menu to cart
  const addMenu = (
    menu: Menu,
    customizations: Array<{
      menuContentId: number;
      mealName?: string;
      extras: Array<{
        ingredient: {
          id: number;
          name: string;
          price: number;
          is_supplement: boolean;
          is_halal: boolean;
          is_vegan: boolean;
          image: string;
        };
        quantity: number;
      }>;
    }>,
    sauces: Array<{ sauce: Sauce; quantity: number }> = [],
    desserts: Array<{ dessert: Dessert; quantity: number }> = [],
    quantity: number = 1,
  ) => {
    // Calculate total price including extras from all meals
    let totalExtrasCost = 0;

    customizations.forEach((customization) => {
      const menuContent = menu.menu_content.find(
        (content) => content.id === customization.menuContentId,
      );

      if (menuContent && menuContent.repas) {
        const meal = menuContent.repas;
        const originalIngredients = parseOriginalIngredients(meal);

        const mealExtrasCost = customization.extras.reduce((total, extra) => {
          const originalQuantity =
            originalIngredients[extra.ingredient.name.toLowerCase()] || 0;
          const additionalQuantity = Math.max(
            0,
            extra.quantity - originalQuantity,
          );
          return total + extra.ingredient.price * additionalQuantity;
        }, 0);

        totalExtrasCost += mealExtrasCost;
      }
    });

    const saucesCost = calculateLimitedExtrasCost(
      sauces.map((s) => ({ price: s.sauce.price, quantity: s.quantity })),
      menu.sauce_number,
    );
    const dessertsCost = calculateLimitedExtrasCost(
      desserts.map((d) => ({ price: d.dessert.price, quantity: d.quantity })),
      menu.dessert_number,
    );

    const unitPrice = menu.price + totalExtrasCost + saucesCost + dessertsCost;

    const newItem: CartItem = {
      id: `menu-${menu.id}-${Date.now()}`,
      type: "menu",
      menu,
      quantity,
      menuCustomizations: customizations,
      selectedSauces: sauces,
      selectedDesserts: desserts,
      totalPrice: unitPrice * quantity,
      addedAt: Date.now(),
    };
    items.value.push(newItem);
  };

  // Remove item from cart
  const removeItem = (itemId: string) => {
    const index = items.value.findIndex((item) => item.id === itemId);
    if (index > -1) {
      items.value.splice(index, 1);
    }
  };

  // Update item quantity
  const updateQuantity = (itemId: string, quantity: number) => {
    const item = items.value.find((item) => item.id === itemId);
    if (item) {
      if (quantity <= 0) {
        removeItem(itemId);
      } else {
        item.quantity = quantity;

        if (item.type === "regular" && item.meal) {
          item.totalPrice = item.meal.price * quantity;
        } else if (item.type === "customized" && item.meal) {
          const originalIngredients = parseOriginalIngredients(item.meal);

          // Calculate cost
          const additionalCost =
            item.extras?.reduce((total, extra) => {
              const originalQuantity =
                originalIngredients[extra.ingredient.name.toLowerCase()] || 0;
              const additionalQuantity = Math.max(
                0,
                extra.quantity - originalQuantity,
              );
              return total + extra.ingredient.price * additionalQuantity;
            }, 0) || 0;

          item.totalPrice = (item.meal.price + additionalCost) * quantity;
        } else if (
          item.type === "menu" &&
          item.menu &&
          item.menuCustomizations
        ) {
          // Recalculate menu price
          let totalExtrasCost = 0;

          item.menuCustomizations.forEach((customization) => {
            const menuContent = item.menu!.menu_content.find(
              (content) => content.id === customization.menuContentId,
            );

            if (menuContent && menuContent.repas) {
              const meal = menuContent.repas;
              const originalIngredients = parseOriginalIngredients(meal);

              const mealExtrasCost = customization.extras.reduce(
                (total, extra) => {
                  const originalQuantity =
                    originalIngredients[extra.ingredient.name.toLowerCase()] ||
                    0;
                  const additionalQuantity = Math.max(
                    0,
                    extra.quantity - originalQuantity,
                  );
                  return total + extra.ingredient.price * additionalQuantity;
                },
                0,
              );

              totalExtrasCost += mealExtrasCost;
            }
          });

          const saucesCost = item.selectedSauces
            ? calculateLimitedExtrasCost(
                item.selectedSauces.map((s) => ({
                  price: s.sauce.price,
                  quantity: s.quantity,
                })),
                item.menu.sauce_number,
              )
            : 0;

          const dessertsCost = item.selectedDesserts
            ? calculateLimitedExtrasCost(
                item.selectedDesserts.map((d) => ({
                  price: d.dessert.price,
                  quantity: d.quantity,
                })),
                item.menu.dessert_number,
              )
            : 0;

          item.totalPrice =
            (item.menu.price + totalExtrasCost + saucesCost + dessertsCost) *
            item.quantity;
        }
      }
    }
  };

  // Update customized meal
  const updateCustomizedMeal = (
    itemId: string,
    extras: Array<{
      ingredient: {
        id: number;
        name: string;
        price: number;
        is_supplement: boolean;
        is_halal: boolean;
        is_vegan: boolean;
        image: string;
      };
      quantity: number;
    }>,
  ) => {
    const item = items.value.find((i) => i.id === itemId);
    if (
      item &&
      (item.type === "customized" || item.type === "regular") &&
      item.meal
    ) {
      // If it was regular, convert to customized
      if (item.type === "regular") {
        item.type = "customized";
      }

      item.extras = extras;

      const originalIngredients = parseOriginalIngredients(item.meal);

      const additionalCost = extras.reduce((total, extra) => {
        const originalQuantity =
          originalIngredients[extra.ingredient.name.toLowerCase()] || 0;
        const additionalQuantity = Math.max(
          0,
          extra.quantity - originalQuantity,
        );
        return total + extra.ingredient.price * additionalQuantity;
      }, 0);

      item.totalPrice = (item.meal.price + additionalCost) * item.quantity;
    }
  };

  // Update menu
  const updateMenu = (
    itemId: string,
    customizations: Array<{
      menuContentId: number;
      mealName?: string;
      extras: Array<{
        ingredient: {
          id: number;
          name: string;
          price: number;
          is_supplement: boolean;
          is_halal: boolean;
          is_vegan: boolean;
          image: string;
        };
        quantity: number;
      }>;
    }>,
    sauces: Array<{ sauce: Sauce; quantity: number }> = [],
    desserts: Array<{ dessert: Dessert; quantity: number }> = [],
  ) => {
    const item = items.value.find((i) => i.id === itemId);
    if (item && item.type === "menu" && item.menu) {
      item.menuCustomizations = customizations;
      item.selectedSauces = sauces;
      item.selectedDesserts = desserts;

      // Recalculate price
      let totalExtrasCost = 0;

      customizations.forEach((customization) => {
        const menuContent = item.menu!.menu_content.find(
          (content) => content.id === customization.menuContentId,
        );

        if (menuContent && menuContent.repas) {
          const meal = menuContent.repas;
          const originalIngredients = parseOriginalIngredients(meal);

          const mealExtrasCost = customization.extras.reduce((total, extra) => {
            const originalQuantity =
              originalIngredients[extra.ingredient.name.toLowerCase()] || 0;
            const additionalQuantity = Math.max(
              0,
              extra.quantity - originalQuantity,
            );
            return total + extra.ingredient.price * additionalQuantity;
          }, 0);

          totalExtrasCost += mealExtrasCost;
        }
      });

      const saucesCost = calculateLimitedExtrasCost(
        sauces.map((s) => ({ price: s.sauce.price, quantity: s.quantity })),
        item.menu.sauce_number,
      );
      const dessertsCost = calculateLimitedExtrasCost(
        desserts.map((d) => ({ price: d.dessert.price, quantity: d.quantity })),
        item.menu.dessert_number,
      );

      item.totalPrice =
        (item.menu.price + totalExtrasCost + saucesCost + dessertsCost) *
        item.quantity;
    }
  };

  // Clear entire cart
  const updateNote = (itemId: string, note: string) => {
    const item = items.value.find((i) => i.id === itemId);
    if (item) {
      item.note = note;
    }
  };

  const clearCart = () => {
    items.value = [];
    deliveryInfo.value = null;
  };

  const setDeliveryInfo = (info: {
    firstName: string;
    lastName: string;
    phone: string;
    email: string;
    address: {
      street: string;
      postalCode: string;
      city: string;
      instructions?: string;
    };
  }) => {
    deliveryInfo.value = info;
  };

  // Watch deliveryInfo too
  watch(deliveryInfo, saveCart, { deep: true });

  // Get cart totals
  const totalItems = computed(() =>
    items.value.reduce((total, item) => total + item.quantity, 0),
  );

  const totalPrice = computed(() =>
    items.value.reduce((total, item) => total + item.totalPrice, 0),
  );

  // Toggle cart drawer
  const toggleCart = () => {
    isOpen.value = !isOpen.value;
  };

  const openCart = () => {
    isOpen.value = true;
  };

  const closeCart = () => {
    isOpen.value = false;
  };

  // Add item from history
  const addFromHistory = (item: CartItem) => {
    // Generate new ID to avoid conflicts
    const newItem = {
      ...item,
      id: `${item.type}-${
        item.meal?.id || item.menu?.id
      }-${Date.now()}-${Math.random()}`,
      addedAt: Date.now(),
    };
    items.value.push(newItem);
  };

  return {
    // State
    items: readonly(items),
    isOpen: readonly(isOpen),
    deliveryInfo: readonly(deliveryInfo),

    // Computed
    totalItems,
    totalPrice,

    // Actions
    addRegularMeal,
    addCustomizedMeal,
    addMenu,
    removeItem,
    updateQuantity,
    updateCustomizedMeal,
    updateMenu,
    updateNote,
    setDeliveryInfo,
    clearCart,
    toggleCart,
    openCart,
    closeCart,
    loadCart,
    saveCart,
    addFromHistory,
  };
});
